import type { SettingsTemplateModelType } from '@/entities/settings-template'
import type {
	SRT_ColumnFiltersState,
	SRT_PaginationState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { SettingsTemplateModel, ValueFilterRule } from '@/shared/generated'
import type { Updater } from '@tanstack/react-table'

import React, { useMemo, useState } from 'react'
import { parseAsInteger, useQueryState } from 'nuqs'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import { SettingsTemplateActions } from '@/entities/settings-template'

import { SettingsTemplateGenaratedShortDescription } from '@/widgets/settings-template-description'

import { DatetimeField } from '@/shared/components/data-grid/fields'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	createSRTColumnHelper,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Operators } from '@/shared/components/query-builder'
import { LinkButton } from '@/shared/components/ui/link-button'
import { getMasterTableLocale } from '@/shared/i18n'
import { useModals } from '@/shared/lib/modal-manager'

export function useSettingsTemplatesTable({
	filters,
	setFilters,
	pageCount,
	data,
	isLoading,
}: {
	filters: ValueFilterRule[]
	setFilters: (filters: ValueFilterRule[]) => void
	pageCount?: number
	isLoading?: boolean
	data: SettingsTemplateModel[]
}) {
	const columns = useColumns()
	const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
	const [perPage, setPerPage] = useQueryState(
		'limit',
		parseAsInteger.withDefault(10)
	)
	const pagination: SRT_PaginationState = useMemo(
		() => ({
			pageIndex: page - 1, // zero-based index -> one-based index
			pageSize: perPage,
		}),
		[page, perPage]
	)
	const initialColumnFilters: SRT_ColumnFiltersState = useMemo(
		() =>
			filters.map((filter) => ({
				id: filter.name,
				value: filter.value,
			})),
		[filters]
	)
	const [columnFilters, setColumnFilters] =
		React.useState<SRT_ColumnFiltersState>(initialColumnFilters)

	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			id: 'updated_at',
			desc: true,
		},
	])

	const onPaginationChange = React.useCallback(
		(updaterOrValue: Updater<SRT_PaginationState>) => {
			if (typeof updaterOrValue === 'function') {
				const newPagination = updaterOrValue(pagination)
				void setPage(newPagination.pageIndex + 1)
				void setPerPage(newPagination.pageSize)
			} else {
				void setPage(updaterOrValue.pageIndex + 1)
				void setPerPage(updaterOrValue.pageSize)
			}
		},
		[pagination, setPage, setPerPage]
	)
	const debouncedSetFilterValues = useDebounceCallback(
		(values: typeof filters) => {
			void setPage(1)
			void setFilters(values)
		},
		1000
	)
	const onColumnFiltersChange = React.useCallback(
		(updaterOrValue: Updater<SRT_ColumnFiltersState>) => {
			setColumnFilters((prev) => {
				const next =
					typeof updaterOrValue === 'function'
						? updaterOrValue(prev)
						: updaterOrValue

				const filterUpdates = next.map(
					(filter) => ({
						name: filter.id,
						operation: Operators.CONTAINS,
						value: filter.value as string | string[],
					}),
					{}
				)

				debouncedSetFilterValues(filterUpdates as ValueFilterRule[])

				return next
			})
		},
		[debouncedSetFilterValues]
	)

	return useMasterReactTable<SettingsTemplateModel>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			sorting,
			isLoading,
			columnFilters,
			pagination,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		enableColumnFilters: true,
		divideX: false,
		enableColumnPinning: false,
		enableVirtualization: false,
		manualFiltering: true,
		manualPagination: true,
		pageCount,
		getRowHeight: () => 100,
		getRowCanExpand: () => true,
		onSortingChange: setSorting,
		layoutMode: 'grid',
		enableColumnActions: false,
		displayColumnDefOptions: {
			'srt-row-actions': {
				header: '',
			},
		},
		renderRowActions(props) {
			return (
				<div className='flex items-center justify-end space-x-1'>
					<SettingsTemplateActions template={props.row.original} />
				</div>
			)
		},
		enableRowActions: true,
		positionActionsColumn: 'last',
		onColumnFiltersChange,
		onPaginationChange,
	})
}
const columnHelper = createSRTColumnHelper<SettingsTemplateModel>()
function useColumns() {
	const { t } = useTranslation()

	return useMemo(
		() => [
			columnHelper.accessor('name', {
				enableSorting: true,
				enableColumnFilter: true,
				meta: {
					label: t('app.name', { defaultValue: 'Name' }),
					placeholder: t('general_search'),
					variant: 'text',
				},
				header: t('app.name', { defaultValue: 'Name' }),
				Cell: ({ cellValue, row }) => {
					const { showModal } = useModals()

					return (
						<div className='flex items-start py-2'>
							<div className='mt-0.5 flex flex-col gap-1 truncate'>
								<LinkButton
									variant='brand'
									className='truncate'
									onClick={() =>
										showModal('SETTINGS_TEMPLATE_MODAL', {
											id: row.original.id,
											template_type: row.original.template_type,
										})
									}
								>
									{cellValue}
								</LinkButton>
								<div className='truncate'>
									<ErrorBoundary>
										<SettingsTemplateGenaratedShortDescription
											template={row.original as SettingsTemplateModelType}
										/>
									</ErrorBoundary>
								</div>
							</div>
						</div>
					)
				},
			}),
			columnHelper.accessor('template_type', {
				size: 220,
				enableSorting: true,
				enablePinning: false,
				header: t('template_type', { defaultValue: 'Template type' }),
				Cell: ({ cellValue }) => {
					const { t } = useTranslation()

					return <span>{t(`template.type.${cellValue}`)}</span>
				},
			}),
			columnHelper.accessor('created_at', {
				size: 120,
				enableSorting: true,
				enablePinning: false,
				Cell: ({ cellValue }) => <DatetimeField value={cellValue} />,
				header: t('created_at', { defaultValue: 'Created at' }),
			}),
			columnHelper.accessor('updated_at', {
				enableSorting: true,
				enablePinning: false,
				size: 120,
				header: t('updated_at', { defaultValue: 'Updated at' }),
				Cell: ({ cellValue }) => <DatetimeField value={cellValue} />,
			}),
		],
		[]
	)
}
